// SL.L3a (#2959, ruling D8; recon Q5) — the Phase 4 card's sprint actions.
// One compact row per sprint that is not closed (name, state, and that
// sprint's SprintActionBar) and one PlanNextSprintButton from GET /sprints'
// project-level allowedActions (W5). Both are reused unchanged: every button
// is rendered if and only if the server lists its action (W1).
//
// reload re-reads /sprints, and asks the page to re-read its open-issue count
// when `issues` (after a close). A failed read shows a quiet error. A response
// for a previous project never lands (the Board's rule, BoardPage.tsx).
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSprints, type Sprint } from '../../lib/sprintsApi';
import SprintActionBar, { type BoardNotice } from './SprintActionBar';
import PlanNextSprintButton from '../PlanNextSprintButton';

export interface Phase4SprintActionsProps {
  projectId: string;
  /** Re-read what the page shows about issues (the open-issue count). */
  onIssuesChanged?: () => void;
}

const text = { fontFamily: 'var(--font-body)', fontSize: 13 } as const;

export default function Phase4SprintActions({ projectId, onIssuesChanged }: Phase4SprintActionsProps) {
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [projectActions, setProjectActions] = useState<string[] | undefined>(undefined);
  const [loadFailed, setLoadFailed] = useState(false);
  const [notice, setNotice] = useState<BoardNotice | null>(null);
  const projectRef = useRef(projectId);
  useEffect(() => { projectRef.current = projectId; }, [projectId]);

  // Another project starts empty. Adjusted during render, not in an effect.
  const [sprintsFor, setSprintsFor] = useState(projectId);
  if (sprintsFor !== projectId) {
    setSprintsFor(projectId);
    setSprints([]);
    setProjectActions(undefined);
    setLoadFailed(false);
    setNotice(null);
  }

  useEffect(() => {
    let ignore = false;
    fetchSprints(projectId).then(
      (d) => { if (!ignore) { setSprints(d?.sprints ?? []); setProjectActions(d?.allowedActions); } },
      () => { if (!ignore) setLoadFailed(true); },
    );
    return () => { ignore = true; };
  }, [projectId]);

  // Never rejects (SprintActionBar's contract).
  const reload = useCallback(async ({ issues }: { issues: boolean }) => {
    const live = () => projectRef.current === projectId;
    try {
      const d = await fetchSprints(projectId);
      if (live()) { setSprints(d?.sprints ?? []); setProjectActions(d?.allowedActions); setLoadFailed(false); }
    } catch {
      if (live()) setLoadFailed(true);
    }
    if (issues && live()) onIssuesChanged?.();
  }, [projectId, onIssuesChanged]);

  const announce = useCallback((n: BoardNotice) => {
    if (projectRef.current === projectId) setNotice(n);
  }, [projectId]);

  const open = sprints.filter((s) => s.state !== 'closed');

  return (
    <div className="mt-3" data-testid="phase4-sprint-actions">
      {loadFailed && <p style={{ ...text, fontSize: 12, color: 'var(--text-secondary)' }}>{"The sprints couldn't be loaded."}</p>}
      {open.map((s) => (
        <div key={s.id} data-testid="phase4-sprint-row" className="flex items-center gap-3 flex-wrap mt-2">
          <span data-testid="phase4-sprint-label" style={{ ...text, color: 'var(--text)', fontWeight: 600 }}>{`${s.name} · ${s.state}`}</span>
          <SprintActionBar projectId={projectId} sprint={s} reload={reload} onNotice={announce} />
        </div>
      ))}
      <div className="mt-2">
        <PlanNextSprintButton projectId={projectId} allowedActions={projectActions} onPlanned={() => reload({ issues: false })} />
      </div>
      {notice && (
        <p role={notice.error ? 'alert' : 'status'} className="mt-2" style={{ ...text, fontSize: 12, color: notice.error ? 'var(--error)' : 'var(--text-secondary)' }}>
          {notice.text}
        </p>
      )}
    </div>
  );
}
